import React, { useState, useEffect, useCallback } from 'react';
import SelectBuscable from '../components/SelectBuscable';
import { useCompany } from '../CompanyContext';
import { useImpersonation } from '../ImpersonationContext';
import { hasPlanAccess, PLAN_FEATURES } from '../lib/pricing';
import UpgradeWall from '../components/UpgradeWall';
import {
  SnookTipoTarea,
  SnookSexo,
  obtenerDistanciasDisponibles,
  obtenerAlturasDisponibles,
  obtenerFrecuenciasDisponibles,
  evaluarTransporte,
  evaluarEmpujeArrastre,
  ResultadoSnookTransporte,
  ResultadoSnookEmpujeArrastre,
} from '../lib/snook-ciriello';
import {
  createSnookCirielloAssessment,
  fetchSnookCirielloAssessments,
  SnookCirielloAssessmentRow,
  SnookCirielloInputs,
  SnookCirielloResultado,
} from '../lib/gestion-tecnica-snook-ciriello';
import { generateSnookCirielloReportPDF } from '../lib/gestion-tecnica-snook-ciriello-pdf';
import { fetchJobPositionsFull, JobPositionLegalFields } from '../lib/gestion-tecnica-constructor';

// Mismos estilos/paleta que RebaMethodologyPage.tsx (fondo oscuro, números principales en 34px).
const inputStyle: React.CSSProperties = {
  width: '100%', background: '#0f172a', border: '1px solid #334155',
  borderRadius: 6, color: '#e2e8f0', padding: '8px 10px', marginTop: 4,
};

const labelStyle: React.CSSProperties = {
  display: 'block', marginBottom: 4, color: '#e2e8f0', fontWeight: 600, fontSize: 13,
};

const cardStyle: React.CSSProperties = {
  background: '#0f172a', border: '1px solid #334155', borderRadius: 10, padding: 16, marginBottom: 20,
};

const checkboxLabelStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', cursor: 'pointer', color: '#e2e8f0', fontSize: 13,
};

const fieldDescStyle: React.CSSProperties = {
  fontSize: 12.5, color: '#94a3b8', margin: '0 0 8px 0', lineHeight: 1.45,
};

// Colores por resultado binario (percentil 90 — aceptable / no aceptable).
const ACEPTABILIDAD_COLORES: Record<'aceptable' | 'no-aceptable', { bg: string; border: string; text: string }> = {
  'aceptable': { bg: 'rgba(47,191,113,0.12)', border: 'rgba(47,191,113,0.3)', text: '#2FBF71' },
  'no-aceptable': { bg: 'rgba(239,68,68,0.12)', border: 'rgba(239,68,68,0.3)', text: '#ef4444' },
};

function ResultadoIntermedio({ label, valor }: { label: string; valor: number }) {
  return (
    <div>
      <span style={{ color: '#94a3b8', fontSize: 12 }}>{label}</span>
      <div style={{ fontSize: 20, fontWeight: 600, color: '#e2e8f0' }}>{valor}</div>
    </div>
  );
}

export default function SnookCirielloMethodologyPage() {
  const { company, profile } = useCompany();
  const { impersonated }     = useImpersonation();
  const displayCompany       = impersonated ?? company;
  const companyId            = displayCompany?.id ?? '';
  const isSuperAdmin         = profile?.role === 'superadmin';

  // ── Plan gate (after all hooks, mismo criterio que RULA/REBA) ──
  const planAllowed = isSuperAdmin || !displayCompany || hasPlanAccess(displayCompany.plan, 'metodologia-snook-ciriello');

  const [jobPositions, setJobPositions] = useState<JobPositionLegalFields[]>([]);
  const [savedAssessments, setSavedAssessments] = useState<SnookCirielloAssessmentRow[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [descripcion, setDescripcion] = useState('');
  const [selectedJobPositionId, setSelectedJobPositionId] = useState('');

  const [tipoTarea, setTipoTarea] = useState<SnookTipoTarea>('transporte');
  const [sexo, setSexo] = useState<SnookSexo>('masculino');
  const [distancia, setDistancia] = useState<string>('');
  const [altura, setAltura] = useState<string>('');
  const [frecuencia, setFrecuencia] = useState<string>('');
  const [sinAsasOAgarreDeficiente, setSinAsasOAgarreDeficiente] = useState(false);
  const [cargaAlejadaDelCuerpo, setCargaAlejadaDelCuerpo] = useState(false);

  const [pesoReal, setPesoReal] = useState('0');
  const [fuerzaInicialReal, setFuerzaInicialReal] = useState('0');
  const [fuerzaSostenidaReal, setFuerzaSostenidaReal] = useState('0');

  const [guardando, setGuardando] = useState(false);
  const [guardadoOk, setGuardadoOk] = useState(false);
  const [ultimaEvaluacionGuardada, setUltimaEvaluacionGuardada] = useState<SnookCirielloAssessmentRow | null>(null);
  const [descargandoPdf, setDescargandoPdf] = useState(false);

  const loadData = useCallback(async () => {
    if (!companyId) { setLoadingData(false); return; }
    setLoadingData(true);
    try {
      const [positions, assessments] = await Promise.all([
        fetchJobPositionsFull(companyId),
        fetchSnookCirielloAssessments(companyId),
      ]);
      setJobPositions(positions);
      setSavedAssessments(assessments);
    } catch (e: any) {
      setError(e.message ?? 'Error al cargar datos');
    } finally {
      setLoadingData(false);
    }
  }, [companyId]);

  useEffect(() => { loadData(); }, [loadData]);

  // ===== Cascada de opciones dependientes: tipoTarea/sexo → distancia → altura/frecuencia =====
  const distanciasDisponibles = obtenerDistanciasDisponibles(tipoTarea, sexo);
  const alturasDisponibles = obtenerAlturasDisponibles(tipoTarea, sexo, distancia);
  const frecuenciasDisponibles = obtenerFrecuenciasDisponibles(tipoTarea, sexo, distancia);

  useEffect(() => {
    const opciones = obtenerDistanciasDisponibles(tipoTarea, sexo);
    if (!opciones.includes(distancia)) {
      setDistancia(opciones[0] ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipoTarea, sexo]);

  useEffect(() => {
    const opcionesAltura = obtenerAlturasDisponibles(tipoTarea, sexo, distancia);
    if (!opcionesAltura.includes(altura)) {
      setAltura(opcionesAltura[0] ?? '');
    }
    const opcionesFrecuencia = obtenerFrecuenciasDisponibles(tipoTarea, sexo, distancia);
    if (!opcionesFrecuencia.includes(frecuencia)) {
      setFrecuencia(opcionesFrecuencia[0] ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [distancia, tipoTarea, sexo]);

  // ===== Cálculo en vivo (sin botón "Calcular"), ramificado según tipoTarea =====
  const pesoRealNum = parseFloat(pesoReal) || 0;
  const fuerzaInicialRealNum = parseFloat(fuerzaInicialReal) || 0;
  const fuerzaSostenidaRealNum = parseFloat(fuerzaSostenidaReal) || 0;

  const resultado: ResultadoSnookTransporte | ResultadoSnookEmpujeArrastre | null = tipoTarea === 'transporte'
    ? evaluarTransporte(sexo, distancia, altura, frecuencia, pesoRealNum, sinAsasOAgarreDeficiente, cargaAlejadaDelCuerpo)
    : evaluarEmpujeArrastre(tipoTarea, sexo, distancia, altura, frecuencia, fuerzaInicialRealNum, fuerzaSostenidaRealNum, sinAsasOAgarreDeficiente, cargaAlejadaDelCuerpo);

  const puedeGuardar = descripcion.trim().length > 0;

  const handleGuardar = async () => {
    setError(null);
    if (!descripcion.trim()) { setError('Ingresa una descripción del local/puesto/tarea evaluada'); return; }
    if (resultado === null) { setError('Selecciona todos los campos para calcular el resultado antes de guardar'); return; }

    setGuardando(true);
    setGuardadoOk(false);
    try {
      const inputs: SnookCirielloInputs = {
        distancia, altura, frecuencia, sinAsasOAgarreDeficiente, cargaAlejadaDelCuerpo,
        ...(tipoTarea === 'transporte'
          ? { pesoReal: pesoRealNum }
          : { fuerzaInicialReal: fuerzaInicialRealNum, fuerzaSostenidaReal: fuerzaSostenidaRealNum }),
      };
      const resultadoAGuardar: SnookCirielloResultado = resultado;

      await createSnookCirielloAssessment({
        companyId,
        jobPositionId: selectedJobPositionId || undefined,
        descripcion,
        tipoTarea,
        sexo,
        inputs,
        resultado: resultadoAGuardar,
        createdBy: profile?.id ?? null,
      });
      // createSnookCirielloAssessment() no retorna el registro insertado (mismo
      // patrón que createRebaAssessment()) — se vuelve a consultar para obtener
      // el registro recién creado con su id real, necesario para el PDF.
      const assessments = await fetchSnookCirielloAssessments(companyId);
      setSavedAssessments(assessments);
      setUltimaEvaluacionGuardada(assessments[0] ?? null);
      setGuardadoOk(true);
    } catch (e: any) {
      setError(e.message ?? 'Error al guardar la evaluación');
    } finally {
      setGuardando(false);
    }
  };

  const handleDescargarPdf = async () => {
    if (!ultimaEvaluacionGuardada) {
      setError('No hay una evaluación guardada para generar el PDF');
      return;
    }
    setDescargandoPdf(true);
    try {
      await generateSnookCirielloReportPDF(
        ultimaEvaluacionGuardada,
        companyId,
        displayCompany?.name ?? 'Empresa',
        profile?.id ?? null
      );
    } catch (e: any) {
      setError(e.message ?? 'Error al generar el PDF');
    } finally {
      setDescargandoPdf(false);
    }
  };

  if (!planAllowed) {
    return <UpgradeWall {...PLAN_FEATURES['metodologia-snook-ciriello']} />;
  }

  if (loadingData) return <div style={{ padding: 24, color: '#94a3b8' }}>Cargando…</div>;

  const resultadoTransporte = tipoTarea === 'transporte' && resultado ? resultado as ResultadoSnookTransporte : null;
  const resultadoEmpujeArrastre = tipoTarea !== 'transporte' && resultado ? resultado as ResultadoSnookEmpujeArrastre : null;
  const colores = resultado ? ACEPTABILIDAD_COLORES[resultado.aceptable ? 'aceptable' : 'no-aceptable'] : null;

  return (
    <div style={{ padding: 24, maxWidth: 900 }}>
      <h1 style={{ fontSize: 22, marginBottom: 6, color: '#e2e8f0' }}>Manipulación de Cargas — Tablas de Snook y Ciriello</h1>
      <p style={{ fontSize: 13, color: '#94a3b8', marginBottom: 20 }}>
        Snook, S.H. y Ciriello, V.M. (1991) — valores del percentil 90, según la calculadora oficial del INSST
        "Manipulación manual: empuje, arrastre y transporte" (2020, NIPO 118-20-079-6).
      </p>

      {error && (
        <div style={{ background: '#7f1d1d', color: '#fecaca', padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 13 }}>
          {error}
        </div>
      )}

      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle}>Descripción (local, puesto o tarea evaluada)</label>
        <input
          style={inputStyle}
          type="text"
          value={descripcion}
          onChange={e => setDescripcion(e.target.value)}
          placeholder="Ej. Transporte de cajas a bodega, Empuje de carro de carga..."
        />
      </div>

      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle}>Puesto de trabajo (opcional)</label>
        <SelectBuscable inputStyle={{ ...inputStyle, paddingRight: 32 }} ariaLabel="Puesto de trabajo (opcional)" value={selectedJobPositionId}
          onChange={setSelectedJobPositionId} vacio="— Sin asociar —" opciones={jobPositions.map(p => ({ value: p.id, label: p.name }))} />
      </div>

      {/* ===== Tipo de tarea y sexo ===== */}
      <div style={cardStyle}>
        <h2 style={{ fontSize: 14, color: '#e2e8f0', marginTop: 0, marginBottom: 12 }}>Tipo de tarea y sexo</h2>

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Tipo de tarea</label>
          <select style={inputStyle} value={tipoTarea} onChange={e => setTipoTarea(e.target.value as SnookTipoTarea)}>
            <option value="transporte" style={{ color: '#000' }}>Transporte</option>
            <option value="empuje" style={{ color: '#000' }}>Empuje</option>
            <option value="arrastre" style={{ color: '#000' }}>Arrastre</option>
          </select>
        </div>

        <div>
          <label style={labelStyle}>Sexo</label>
          <select style={inputStyle} value={sexo} onChange={e => setSexo(e.target.value as SnookSexo)}>
            <option value="masculino" style={{ color: '#000' }}>Masculino</option>
            <option value="femenino" style={{ color: '#000' }}>Femenino</option>
          </select>
        </div>
      </div>

      {/* ===== Condiciones de la tarea ===== */}
      <div style={cardStyle}>
        <h2 style={{ fontSize: 14, color: '#e2e8f0', marginTop: 0, marginBottom: 12 }}>Condiciones de la tarea</h2>

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Distancia</label>
          <select style={inputStyle} value={distancia} onChange={e => setDistancia(e.target.value)}>
            {distanciasDisponibles.map(d => <option key={d} value={d} style={{ color: '#000' }}>{d} m</option>)}
          </select>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Altura</label>
          <select style={inputStyle} value={altura} onChange={e => setAltura(e.target.value)}>
            {alturasDisponibles.map(a => <option key={a} value={a} style={{ color: '#000' }}>{a} cm</option>)}
          </select>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Frecuencia</label>
          <select style={inputStyle} value={frecuencia} onChange={e => setFrecuencia(e.target.value)}>
            {frecuenciasDisponibles.map(f => <option key={f} value={f} style={{ color: '#000' }}>{f}</option>)}
          </select>
        </div>

        <p style={fieldDescStyle}>
          Correcciones del método general (Snook &amp; Ciriello, 1991), aplicadas sobre el valor tabulado antes de comparar con el valor real.
        </p>
        <label style={checkboxLabelStyle}>
          <input type="checkbox" checked={sinAsasOAgarreDeficiente}
            onChange={e => setSinAsasOAgarreDeficiente(e.target.checked)} />
          Carga sin asas o con agarre deficiente (-15%)
        </label>
        <label style={checkboxLabelStyle}>
          <input type="checkbox" checked={cargaAlejadaDelCuerpo}
            onChange={e => setCargaAlejadaDelCuerpo(e.target.checked)} />
          Carga manejada alejada del cuerpo (-50%)
        </label>

        {tipoTarea === 'transporte' ? (
          <div style={{ marginTop: 16 }}>
            <label style={labelStyle}>Peso real de la carga (kg)</label>
            <input style={inputStyle} type="number" value={pesoReal} onChange={e => setPesoReal(e.target.value)} />
          </div>
        ) : (
          <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>Fuerza inicial real (kgf)</label>
              <input style={inputStyle} type="number" value={fuerzaInicialReal} onChange={e => setFuerzaInicialReal(e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>Fuerza sostenida real (kgf)</label>
              <input style={inputStyle} type="number" value={fuerzaSostenidaReal} onChange={e => setFuerzaSostenidaReal(e.target.value)} />
            </div>
          </div>
        )}
      </div>

      {/* ===== Resultado ===== */}
      <div style={cardStyle}>
        <h2 style={{ fontSize: 14, color: '#e2e8f0', marginTop: 0, marginBottom: 12 }}>Resultado</h2>

        {resultado === null ? (
          <p style={{ color: '#94a3b8', fontSize: 13 }}>Selecciona todos los campos para ver el resultado.</p>
        ) : (
          <>
            {resultadoTransporte && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginBottom: 16 }}>
                <ResultadoIntermedio label="Peso máximo aceptable (kg)" valor={resultadoTransporte.maximoAceptable} />
                <ResultadoIntermedio label="Peso real (kg)" valor={resultadoTransporte.pesoReal} />
              </div>
            )}

            {resultadoEmpujeArrastre && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginBottom: 12 }}>
                  <ResultadoIntermedio label="Fuerza inicial máxima (kgf)" valor={resultadoEmpujeArrastre.inicialMaxima} />
                  <ResultadoIntermedio label="Fuerza inicial real (kgf)" valor={resultadoEmpujeArrastre.fuerzaInicialReal} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
                  <ResultadoIntermedio label="Fuerza sostenida máxima (kgf)" valor={resultadoEmpujeArrastre.sostenidaMaxima} />
                  <ResultadoIntermedio label="Fuerza sostenida real (kgf)" valor={resultadoEmpujeArrastre.fuerzaSostenidaReal} />
                </div>
              </div>
            )}

            {colores && (
              <div
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8,
                  background: colores.bg, border: `1px solid ${colores.border}`,
                }}
              >
                <strong style={{ color: colores.text, fontSize: 14 }}>
                  {resultado.aceptable ? 'Aceptable' : 'No aceptable'}
                </strong>
              </div>
            )}
          </>
        )}
      </div>

      <button
        onClick={handleGuardar}
        disabled={guardando || !puedeGuardar}
        style={{
          background: '#2FBF71', color: '#0f172a', border: 'none', borderRadius: 6,
          padding: '8px 16px', fontWeight: 600, cursor: guardando || !puedeGuardar ? 'not-allowed' : 'pointer',
          opacity: guardando || !puedeGuardar ? 0.6 : 1,
        }}
      >
        {guardando ? 'Guardando...' : 'Guardar evaluación'}
      </button>

      {guardadoOk && (
        <div style={{ color: '#2FBF71', fontSize: 13, marginTop: 10 }}>✓ Evaluación guardada correctamente</div>
      )}

      {guardadoOk && ultimaEvaluacionGuardada && (
        <button
          onClick={handleDescargarPdf}
          disabled={descargandoPdf}
          style={{
            background: '#3b82f6', color: '#0f172a', border: 'none', borderRadius: 6,
            padding: '8px 16px', fontWeight: 600, cursor: descargandoPdf ? 'not-allowed' : 'pointer',
            opacity: descargandoPdf ? 0.6 : 1, marginTop: 10, marginLeft: 10,
          }}
        >
          {descargandoPdf ? 'Generando PDF...' : 'Descargar reporte PDF'}
        </button>
      )}

      {savedAssessments.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <h2 style={{ fontSize: 15, color: '#e2e8f0', marginBottom: 8 }}>Evaluaciones guardadas</h2>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, color: '#e2e8f0' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #334155', textAlign: 'left' }}>
                <th style={{ padding: '6px 8px' }}>Descripción</th>
                <th style={{ padding: '6px 8px' }}>Tipo</th>
                <th style={{ padding: '6px 8px' }}>Sexo</th>
                <th style={{ padding: '6px 8px' }}>Resultado</th>
                <th style={{ padding: '6px 8px' }}>Fecha</th>
              </tr>
            </thead>
            <tbody>
              {savedAssessments.map(a => (
                <tr key={a.id} style={{ borderBottom: '1px solid #1e293b' }}>
                  <td style={{ padding: '6px 8px' }}>{a.descripcion}</td>
                  <td style={{ padding: '6px 8px' }}>{a.tipo_tarea.charAt(0).toUpperCase()}{a.tipo_tarea.slice(1)}</td>
                  <td style={{ padding: '6px 8px' }}>{a.sexo === 'masculino' ? 'Masculino' : 'Femenino'}</td>
                  <td style={{ padding: '6px 8px' }}>{a.resultado.aceptable ? 'Aceptable' : 'No aceptable'}</td>
                  <td style={{ padding: '6px 8px' }}>{new Date(a.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
